// The ordered page-slice map behind `lib/paged.ts` — split out the same way
// `paged-view.ts` was, to keep both files under the dashboard's 150-LOC cap, and
// because it has its own reason to change: this owns HOW pages are held, while
// `paged.ts` owns WHEN they are merged, dropped and re-read.
//
// Pages are held BY the cursor that fetched them, in arrival order — not one flat
// accumulator. That is what lets a refetch at a cursor already loaded refresh its
// slice in place instead of appending it twice (an invalidation, a load-more
// retry), and what lets a poll (`paged-poll.ts`) swap the whole window in by the
// cursors that addressed it.

/**
 * Ceiling on how many slices `exhaust` may walk to on its own — a runaway
 * collection (or a server minting the same token forever) can't spin. When it
 * trips `hasMore()` stays true and the manual button takes over: nothing is
 * silently truncated. Re-exported from `paged.ts`, which owns that policy.
 */
export const MAX_AUTO_PAGES = 20;

export type PageSlices<T> = {
  /** Hold `rows` at `cursor`, appending it to the arrival order the first time. */
  readonly hold: (cursor: string | null, rows: readonly T[]) => void;
  /** Every held row, in page order. */
  readonly flatten: () => T[];
  /** Drop every slice — a fresh page one, a `reset()`, or an account change. */
  readonly clear: () => void;
  /** Slices held. `exhaust` bounds its automatic walk on this. */
  readonly count: () => number;
  /**
   * Moves on every `hold`, `clear`, `asked` and landed `replace` — a merge, a
   * drop, a load-more asked for, a `patch()`. A poll walk that saw it move lands nothing: a Load more, a reload,
   * a reset or an account switch each make the walked window the wrong one to
   * swap in (`paged-poll.ts`).
   */
  readonly epoch: () => number;
  /**
   * A page was ASKED for and has not landed: move the epoch now. Without it a
   * walk already out passes the epoch check and re-keys the query off the page
   * the user just asked for, which then never appears.
   */
  readonly asked: () => void;
  /** Replace the first row `match` accepts, inside the slice that holds it. */
  readonly replace: (match: (row: T) => boolean, fresh: T) => void;
};

export function pageSlices<T>(): PageSlices<T> {
  const order: (string | null)[] = [];
  const slices = new Map<string | null, readonly T[]>();
  let epoch = 0;
  return {
    hold: (cursor, rows) => {
      epoch += 1;
      if (!slices.has(cursor)) order.push(cursor);
      slices.set(cursor, rows);
    },
    flatten: () => order.flatMap((at) => [...(slices.get(at) ?? [])]),
    clear: () => {
      epoch += 1;
      order.length = 0;
      slices.clear();
    },
    count: () => order.length,
    epoch: () => epoch,
    asked: () => {
      epoch += 1;
    },
    replace: (match, fresh) => {
      // The SLICE is patched, not just the flattened store: the next merge
      // rebuilds from the slices, so a load-more would otherwise revert the row
      // the drawer just changed back to its stale server copy.
      for (const [at, slice] of slices) {
        const i = slice.findIndex(match);
        if (i < 0) continue;
        const next = [...slice];
        next[i] = fresh;
        slices.set(at, next);
        // A poll walk already out read the pre-patch row; landing it would revert this.
        epoch += 1;
        return;
      }
    },
  };
}
